import React from 'react';
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, MessageSquare, Share2, Heart, Star, TrendingUp } from "lucide-react";

const Community: React.FC = () => {
  const communityFeatures = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Active Developer Network",
      description: "Connect with thousands of developers sharing knowledge, code snippets, and best practices in real-time.",
      stats: "15K+ Members"
    },
    {
      icon: <Share2 className="w-8 h-8" />,
      title: "Resource Sharing Hub",
      description: "Upload, discover, and collaborate on coding resources, templates, and project boilerplates with the community.",
      stats: "5K+ Resources"
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Expert Discussions",
      description: "Engage in meaningful conversations about emerging technologies, coding challenges, and industry trends.",
      stats: "2K+ Daily Posts"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Full Stack Developer",
      avatar: "SC",
      content: "This platform transformed how I learn and share code. The community is incredibly supportive and knowledgeable.",
      rating: 5
    },
    {
      name: "Marcus Rodriguez",
      role: "DevOps Engineer", 
      avatar: "MR",
      content: "Found amazing resources here that saved me weeks of development time. The quality of shared content is outstanding.",
      rating: 5
    },
    {
      name: "Emily Watson",
      role: "Frontend Specialist",
      avatar: "EW", 
      content: "Love the collaborative environment. I've contributed several React components that the community actively uses.",
      rating: 5
    }
  ];

  return (
    <section id="community" className="py-20 bg-gradient-to-br from-purple-50 via-white to-cyan-50">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 bg-clip-text text-transparent mb-6">
            Join Our Thriving Community
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Be part of a vibrant ecosystem where developers collaborate, share resources, and grow together through knowledge exchange and mutual support.
          </p>
        </div>

        {/* Community Features */}
        <div className="mb-20">
          <div className="space-y-8 max-w-4xl mx-auto">
            {communityFeatures.map((feature, index) => (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-300 border-0 bg-white/80 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-purple-500 to-cyan-500 rounded-2xl flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                      {feature.icon}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
                        <h3 className="text-2xl font-bold text-gray-800 mb-2 md:mb-0">
                          {feature.title}
                        </h3>
                        <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-100 to-cyan-100 text-purple-700 rounded-full text-sm font-semibold">
                          <TrendingUp className="w-4 h-4 mr-2" />
                          {feature.stats}
                        </span>
                      </div>
                      <p className="text-gray-600 text-lg leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Community Testimonials */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center text-gray-800 mb-12">
            What Our Community Says
          </h3>
          <div className="space-y-6 max-w-4xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="hover:shadow-xl transition-all duration-300 border-0 bg-white/90 backdrop-blur-sm">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-shrink-0 flex flex-col items-center md:items-start">
                      <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg mb-3">
                        {testimonial.avatar}
                      </div>
                      <div className="flex gap-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                    </div>
                    <div className="flex-1 text-center md:text-left">
                      <p className="text-gray-700 text-lg leading-relaxed mb-4 italic">
                        "{testimonial.content}"
                      </p>
                      <div>
                        <h4 className="font-semibold text-gray-800 text-lg">
                          {testimonial.name}
                        </h4>
                        <p className="text-purple-600 font-medium">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center max-w-2xl mx-auto">
          <Card className="border-0 bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 text-white overflow-hidden">
            <CardContent className="p-10 relative">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="relative z-10">
                <Heart className="w-12 h-12 mx-auto mb-6 animate-pulse" />
                <h3 className="text-3xl font-bold mb-4">
                  Ready to Join Our Community?
                </h3>
                <p className="text-xl mb-8 text-white/90">
                  Start sharing, learning, and growing with fellow developers today. Your next breakthrough is just a conversation away.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    className="bg-white text-purple-600 hover:bg-gray-100 font-semibold px-8 py-3 text-lg"
                  >
                    Join Community
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-white text-white hover:bg-white hover:text-purple-600 font-semibold px-8 py-3 text-lg"
                  >
                    Explore Resources
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Community;